import type { StructuredCollectionConfig } from '@ltvco/refresh-lib/hooks';

/**
 * Collections used by the Death Index Records.
 *
 * IMPORTANT:
 * - The order of this list must match the order defined in Remoulade.
 * - Do not reorder existing collections, as the position is used to maintain
 *   consistency with Remoulade mappings and processing logic.
 * - When adding a new collection, append it to the end of the array.
 * - Existing entries should remain in their current positions.
 */
export const DEATH_INDEX_COLLECTIONS: StructuredCollectionConfig[] = [
  {
    id: '0000011',
    plainName: 'New York Death Index, 1957-1972',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ny_death_index',
    state: 'NY',
    yearStart: 1957,
    yearEnd: 1972,
  },
  {
    id: '0000004',
    plainName: 'California Death Index, 1940-1997',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ca_death_index',
    state: 'CA',
    yearStart: 1940,
    yearEnd: 1997,
    overrides: [
      {
        source: 'birthDate',
        target: 'event_secondary_date_full',
        modifiers: [{ name: 'dateToDateRange' }],
      },
    ],
  },
  {
    id: '0000065',
    plainName: 'Tennessee Death Index, 1949-2014',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'tn_death_index',
    state: 'TN',
    yearStart: 1949,
    yearEnd: 2014,
    options: { combineFirstMiddleName: true },
  },
  {
    id: '0000034',
    plainName: 'Nebraska Death Index, 1969-2019',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ne_death_index',
    state: 'NE',
    yearStart: 1969,
    yearEnd: 2019,
    overrides: [
      {
        source: 'birthDate',
        target: 'event_secondary_date_full',
        modifiers: [{ name: 'dateToDateRange' }],
      },
    ],
  },
  {
    id: '0000064',
    plainName: 'Minnesota Death Index, 1904-2001',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'mn_death_index',
    state: 'MN',
    yearStart: 1904,
    yearEnd: 2001,
    options: { combineFirstMiddleName: true },
  },
  {
    id: '0000066',
    plainName: 'Texas Death Index, 1965-1998',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'tx_death_index',
    state: 'TX',
    yearStart: 1965,
    yearEnd: 1998,
  },
  {
    id: '0000138',
    plainName: 'Vermont Death Index, 1909-2024',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'vt_death_index',
    state: 'VT',
    yearStart: 1909,
    yearEnd: 2024,
  },
  {
    id: '0000067',
    plainName: 'Texas Death Index, 1999-2022',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'tx_death_index_1999_2022',
    state: 'TX',
    yearStart: 1999,
    yearEnd: 2022,
  },
  {
    id: '0000128',
    plainName: 'Maine Death Index, 1956-2009',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'me_death_index',
    state: 'ME',
    yearStart: 1956,
    yearEnd: 2009,
    options: { combineFirstMiddleName: true },
  },
  {
    id: '0000140',
    plainName: 'Louisiana Death Index, 1819-1973',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'la_death_index',
    state: 'LA',
    yearStart: 1819,
    yearEnd: 1973,
    options: { combineFirstMiddleName: true },
  },
  {
    id: '0000162',
    plainName: 'Arkansas Death Index, 1935-1961',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ar_death_index',
    state: 'AR',
    yearStart: 1935,
    yearEnd: 1961,
    options: { combineFirstMiddleName: true },
  },
  {
    id: '0000029',
    plainName: 'Kentucky Death Index, 1911-1989',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ky_death_index',
    state: 'KY',
    yearStart: 1911,
    yearEnd: 1989,
  },
  {
    id: '0000026',
    plainName: 'New York City, Death Index, 1862-1948',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ny_death_index',
    state: 'NY',
    yearStart: 1862,
    yearEnd: 1948,
    options: { combineFirstMiddleName: true },
    isV2: true,
  },
  {
    id: '0000010',
    plainName: 'New York, Death Index, 1880-1956',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ny_death_index',
    state: 'NY',
    yearStart: 1880,
    yearEnd: 1956,
    options: { combineFirstMiddleName: true },
    isV2: true,
  },
  {
    id: '0000223',
    plainName: 'France, Death Index, 1970-2025',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'fr_death_index',
    country: 'France',
    yearStart: 1970,
    yearEnd: 2025,
    options: { combineFirstMiddleName: true },
    isV2: true,
  },
  {
    // Ohio Obituary Index — treated as a death_index (primary event is Death).
    id: '0000051',
    plainName: 'Ohio, Obituary Index, 1800-2025',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'oh_obituary_index',
    state: 'OH',
    yearStart: 1800,
    yearEnd: 2025,
    // Birth is a searchable secondary event for the obituary.
    overrides: [
      {
        source: 'birthDate',
        target: 'event_secondary_date_full',
        modifiers: [{ name: 'dateToDateRange' }],
      },
    ],
    isV2: true,
  },
  {
    id: '0000018',
    plainName: 'Connecticut, Death Index, 1897-2001',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'ct_death_index',
    state: 'CT',
    yearStart: 1897,
    yearEnd: 2001,
    options: { combineFirstMiddleName: true },
    isV2: true,
  },
  {
    id: '0000168',
    plainName: 'Illinois, Death Index, 1916-1950',
    type: 'death_index',
    source: 'deathIndexSearch',
    reportType: 'death_indexes',
    ga4ReportType: 'il_death_index',
    state: 'IL',
    yearStart: 1916,
    yearEnd: 1950,
    isV2: true,
  },
];
